import React from 'react'
import styled from 'styled-components'
import { cohortsService } from '../services/apiService'

// Add this helper function near the top of the file, after the imports
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch (e) {
    return 'Invalid date';
  }
};

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.75rem;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  transition: all 0.2s;

  &:hover {
    color: #374151;
    background-color: #f3f4f6;
  }
`

const ContentHeader = styled.div`
  margin-bottom: 2rem;
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 1rem;
`

const ContentTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.5rem;
  padding-right: 3rem;
`

const ContentMeta = styled.div`
  display: flex;
  gap: 2rem;
  color: #6b7280;
  font-size: 0.875rem;
  flex-wrap: wrap;
`

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e5e5;
  margin-bottom: 2rem;
  gap: 0.5rem;
`

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  color: #6b7280;
  font-weight: 500;
  transition: all 0.2s;
  border-radius: 0.375rem 0.375rem 0 0;

  &.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
    background-color: #f8fafc;
  }

  &:hover {
    color: #1d4ed8;
    background-color: #f8fafc;
  }
`

const TabContent = styled.div`
  min-height: 400px;
`

const ChunkGrid = styled.div`
  display: grid;
  gap: 1rem;
`

const ChunkCard = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 0.5rem;
  padding: 1.5rem;
  background: #f9fafb;
  transition: border-color 0.2s;

  &:hover {
    border-color: #d1d5db;
  }
`

const ChunkHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
`

const ChunkTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  flex: 1;
`

const ChunkType = styled.span`
  background: #dbeafe;
  color: #1e40af;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
`

const ChunkContent = styled.div`
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  max-height: 300px;
  overflow-y: auto;
  padding: 0.5rem;
  background: white;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
`

const FileSection = styled.div`
  text-align: center;
  padding: 3rem;
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  background: #f9fafb;
`

const DownloadButton = styled.button`
  background-color: #2563eb;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1d4ed8;
  }
`

const SummarySection = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
`

const SummaryTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #0369a1;
  margin-bottom: 1rem;
`

const SummaryContent = styled.div`
  color: #0c4a6e;
  line-height: 1.6;
  white-space: pre-wrap;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
`

const ErrorState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 0.5rem;
  border: 1px solid #fecaca;
`

function ContentViewer({ cohortId, contentId, onClose }) {
  const [content, setContent] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [activeTab, setActiveTab] = React.useState('chunks')

  React.useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true)
        setError(null)
        const contentData = await cohortsService.getContentDetails(cohortId, contentId)
        setContent(contentData)
        
        // Auto-select file tab if no processed content
        const processedContent = contentData.content_data?.processed_content
        if (!processedContent && contentData.file_path) {
          setActiveTab('file')
        }
      } catch (error) {
        console.error('Failed to fetch content details:', error)
        setError('Failed to load content details')
      } finally {
        setLoading(false)
      }
    }

    if (cohortId && contentId) {
      fetchContent()
    }
  }, [cohortId, contentId])

  const downloadFile = async () => {
    try {
      const blob = await cohortsService.getContentFile(cohortId, contentId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = content?.file_path?.split('/').pop() || 'download'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to download file:', error)
      alert('Failed to download file')
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (loading) {
    return (
      <Modal onClick={handleBackdropClick}>
        <ModalContent>
          <CloseButton onClick={onClose}>×</CloseButton>
          <LoadingState>Loading content...</LoadingState>
        </ModalContent>
      </Modal>
    )
  }

  if (error) {
    return (
      <Modal onClick={handleBackdropClick}>
        <ModalContent>
          <CloseButton onClick={onClose}>×</CloseButton>
          <ErrorState>
            <h3>Error Loading Content</h3>
            <p>{error}</p>
          </ErrorState>
        </ModalContent>
      </Modal>
    )
  }

  if (!content) {
    return (
      <Modal onClick={handleBackdropClick}>
        <ModalContent>
          <CloseButton onClick={onClose}>×</CloseButton>
          <ErrorState>Content not found</ErrorState>
        </ModalContent>
      </Modal>
    )
  }

  const processedContent = content.content_data?.processed_content
  const hasProcessedContent = processedContent && processedContent.chunks
  const hasSummary = content.content_data?.content_summary || processedContent?.summary

  return (
    <Modal onClick={handleBackdropClick}>
      <ModalContent>
        <CloseButton onClick={onClose} title="Close">×</CloseButton>
        
        <ContentHeader>
          <ContentTitle>{content.title}</ContentTitle>
          <ContentMeta>
            <span>Type: {content.content_type}</span>
            <span>Uploaded: {formatDate(content.created_at)}</span>
            {processedContent?.metadata?.chunking_strategy && (
              <span>Chunking: {processedContent.metadata.chunking_strategy}</span>
            )}
            {processedContent?.metadata?.compression_ratio && (
              <span>Compression: {processedContent.metadata.compression_ratio}</span>
            )}
          </ContentMeta>
        </ContentHeader>

        {hasSummary && (
          <SummarySection>
            <SummaryTitle>Content Summary</SummaryTitle>
            <SummaryContent>{hasSummary}</SummaryContent>
          </SummarySection>
        )}

        <Tabs>
          {hasProcessedContent && (
            <Tab 
              className={activeTab === 'chunks' ? 'active' : ''}
              onClick={() => setActiveTab('chunks')}
            >
              Smart Chunks ({processedContent.chunks.length})
            </Tab>
          )}
          <Tab 
            className={activeTab === 'file' ? 'active' : ''}
            onClick={() => setActiveTab('file')}
          >
            Original File
          </Tab>
        </Tabs>

        <TabContent>
          {activeTab === 'chunks' && hasProcessedContent && (
            <ChunkGrid>
              {processedContent.chunks.map((chunk, index) => (
                <ChunkCard key={index}>
                  <ChunkHeader>
                    <ChunkTitle>
                      {chunk.title || `Chunk ${index + 1}`}
                    </ChunkTitle>
                    <ChunkType>{chunk.type || 'content'}</ChunkType>
                  </ChunkHeader>
                  <ChunkContent>
                    {chunk.content}
                  </ChunkContent>
                </ChunkCard>
              ))}
            </ChunkGrid>
          )}

          {activeTab === 'file' && (
            <FileSection>
              <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Original File</h3>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                Download the original uploaded file to view it in its native format.
              </p>
              {content.file_path && (
                <DownloadButton onClick={downloadFile}>
                  Download {content.content_type.toUpperCase()} File
                </DownloadButton>
              )}
              {!content.file_path && (
                <p style={{ color: '#ef4444' }}>Original file not available</p>
              )}
            </FileSection>
          )}
        </TabContent>
      </ModalContent>
    </Modal>
  )
}

export default ContentViewer
